/**
 * 직원 생성 Mutations Hook (V16 - 불변식 보장 + 청크 상세 로깅)
 *
 * V16 변경사항 (불변식 강화):
 * - 결과 불변식 강제: totalCount === successCount + failureCount 항상 보장
 * - chunkId 파라미터로 청크 추적 가능
 * - 청크 에러 발생 시 해당 청크의 모든 행을 개별 실패로 기록
 * - 최종 합산 후 불변식 검증 및 보정
 *
 * V15 변경사항 (청크 아키텍처 전환):
 * - bulkImportEmployeesWithChunks: 청크 단위 순차 호출 (권장)
 * - CHUNK_SIZE = 20으로 청크 분할
 * - 프론트엔드에서 청크 나누어 여러 번 Cloud Function 호출
 * - 진행 상태 콜백 지원 (onProgress)
 * - 타임아웃/internal 에러 문제 해결
 *
 * V14 변경사항:
 * - 백엔드 V14 안정화에 맞춰 에러 처리 강화
 * - 타입 안전성 강화 (httpsCallable 제네릭 명시)
 *
 * 직원 생성 관련 Cloud Functions 호출을 위한 커스텀 훅입니다.
 */

import { useState } from 'react';
import { httpsCallable } from 'firebase/functions';
import { functions } from '../../firebase/config';

// ============================================
// V15: 청크 아키텍처 상수
// ============================================

/**
 * V15: 한 청크당 처리할 행 수
 * - 백엔드 MAX_ROWS_PER_CHUNK=30과 맞춤
 * - 안전하게 20으로 설정
 */
const CHUNK_SIZE = 20;

// ============================================
// 타입 정의
// ============================================

/**
 * 학력 코드
 */
export type EducationLevelCode =
  | 'HIGH_SCHOOL'      // 고졸
  | 'ASSOCIATE_2'      // 초대졸 2년
  | 'ASSOCIATE_3'      // 초대졸 3년
  | 'BACHELOR';        // 대졸

/**
 * 국적 타입
 */
export type NationalityType = 'domestic' | 'foreign';

/**
 * 단일 직원 생성 요청 데이터 (V36.8)
 */
export interface CreateEmployeeFormData {
  // ========== 기본 정보 ==========
  nameKo: string;
  email: string;
  employeeNumber: string;
  gender?: 'male' | 'female'; // V2: other 제거
  birthDate?: string; // V36.8: 폼에서는 제거되지만 타입은 유지 (하위 호환)

  // ========== 조직 정보 ==========
  companyName?: string;
  headquarterName?: string;
  divisionName?: string;
  teamName?: string;
  orgUnitId?: string;

  // V62: 소속 (회사/파견업체명)
  affiliationName?: string;

  // ========== 직위/직책 정보 (V2: ID 우선) ==========
  positionId?: string; // V2: 직위 ID (마스터 데이터 기반)
  positionName?: string; // 직위 한글 (백업)
  jobTitleId?: string; // V2: 직책 ID (마스터 데이터 기반)
  jobTitleName?: string; // 직책 한글 (백업)

  // ========== 고용 정보 (V2: 인턴/계약직 추가) ==========
  employmentType?: 'REGULAR' | 'CONTRACT' | 'DISPATCH' | 'INTERN';

  // 정규직
  regularHireDate?: string;

  // 파견직
  dispatchHireDate?: string;
  dispatchEndDate?: string; // V2: 파견 계약 만료일

  // 인턴 (V2 추가)
  internHireDate?: string;
  internEndDate?: string;

  // 계약직 (V2 추가)
  contractHireDate?: string;
  contractEndDate?: string;

  // 기타
  firstHireDate?: string;
  ngleFirstHireDate?: string;

  // ========== 내/외국인 구분 및 민감정보 (V2) ==========
  nationalityType?: NationalityType; // V2: 내국인/외국인 구분
  residentRegistrationNo?: string; // V2: 주민등록번호 (내국인)

  // 하위 호환성
  rrn?: string;

  // ========== 연락처/주소/학력/전공 (V2 추가) ==========
  phoneNumber?: string;
  address?: string;
  educationLevelCode?: EducationLevelCode;
  major?: string;

  // ========== V59: 이전경력 ==========
  priorCareerYears?: number; // 단위: 년, 소수 허용 (예: 0, 0.5, 2, 3.0)

  // ========== V61: 진급 가산 연한 ==========
  promotionBonusYears?: number; // 단위: 정수 (예: 0, 1, 2)

  // ========== V37: 역할(Role) 부여 ==========
  selectedRoleIds?: string[]; // 생성 시 부여할 역할 ID 배열
}

/**
 * 엑셀 일괄 업로드 요청 데이터 (레거시)
 */
export interface BulkImportExcelData {
  rawRows?: any[][]; // 원시 2D 배열 (권장)
  rows?: Array<Record<string, any>>; // 레거시: 이미 매핑된 객체 배열
}

/**
 * V16: 청크 요청 데이터
 */
export interface ChunkImportRequest {
  dataRows: Array<Record<string, any>>; // 이미 파싱된 객체 배열
  baseRowIndex: number; // 원본 엑셀 기준 행 번호 (1-based)
  chunkId: string; // V16: 청크 식별자 (예: "1/5", "2/5") - 로깅용
}

/**
 * 엑셀 일괄 업로드 응답
 */
export interface BulkImportResult {
  totalCount: number;
  successCount: number;
  failureCount: number;
  failures: Array<{
    rowIndex: number;
    reason: string;
  }>;
}

/**
 * V15: 진행 상태 콜백
 */
export interface ImportProgress {
  currentChunk: number;
  totalChunks: number;
  processedRows: number;
  totalRows: number;
}

// ============================================
// V15: 유틸리티 함수
// ============================================

/**
 * V15: 배열을 청크로 분할
 */
function splitIntoChunks<T>(array: T[], chunkSize: number): T[][] {
  const chunks: T[][] = [];
  for (let i = 0; i < array.length; i += chunkSize) {
    chunks.push(array.slice(i, i + chunkSize));
  }
  return chunks;
}

/**
 * V15: 원시 2D 배열에서 헤더 행 인덱스 찾기
 */
function findHeaderRowIndex(rawRows: any[][]): number {
  const NAME_CANDIDATES = ['성명', '이름', 'name', 'nameko', '직원명', '사원명', '한글이름'];
  const EMPLOYEE_NUMBER_CANDIDATES = ['사번', 'employeeid', 'employeenumber', 'id', 'no', '사원번호', '직원번호'];
  const EMAIL_CANDIDATES = ['이메일', 'email', 'e-mail', '메일'];

  for (let i = 0; i < Math.min(rawRows.length, 10); i++) {
    const row = rawRows[i];
    if (!Array.isArray(row)) continue;

    const normalizedCells = row
      .map(cell => (cell ? String(cell).trim().toLowerCase().replace(/\s+/g, '') : ''))
      .filter(cell => cell.length > 0);

    const hasName = normalizedCells.some(cell => NAME_CANDIDATES.includes(cell));
    const hasEmployeeNumber = normalizedCells.some(cell => EMPLOYEE_NUMBER_CANDIDATES.includes(cell));
    const hasEmail = normalizedCells.some(cell => EMAIL_CANDIDATES.includes(cell));

    const matchCount = [hasName, hasEmployeeNumber, hasEmail].filter(Boolean).length;
    if (matchCount >= 2) {
      return i;
    }
  }

  return 0; // 못 찾으면 첫 번째 행
}

/**
 * V15: 원시 2D 배열을 객체 배열로 변환
 */
function convertRawRowsToObjects(
  rawRows: any[][],
  headerRowIndex: number
): Array<Record<string, any>> {
  const headers = rawRows[headerRowIndex] as any[];
  const dataRows: Array<Record<string, any>> = [];

  for (let i = headerRowIndex + 1; i < rawRows.length; i++) {
    const row = rawRows[i];
    if (!Array.isArray(row)) continue;

    // 빈 행 건너뛰기
    const hasData = row.some(cell =>
      cell !== null && cell !== undefined && cell !== ''
    );
    if (!hasData) continue;

    // 객체로 변환
    const obj: Record<string, any> = {};
    headers.forEach((header, index) => {
      if (header) {
        const headerStr = String(header).trim();
        if (headerStr) {
          obj[headerStr] = row[index];
        }
      }
    });

    dataRows.push(obj);
  }

  return dataRows;
}

// ============================================
// 직원 생성 Mutations Hook
// ============================================

/**
 * 직원 생성 Mutations Hook
 */
export function useEmployeeCreateMutations() {
  const [isCreating, setIsCreating] = useState(false);
  const [isImporting, setIsImporting] = useState(false);

  /**
   * 단일 직원 생성
   */
  const createEmployee = async (
    data: CreateEmployeeFormData
  ): Promise<{ userId?: string; employeeNumber?: string }> => {
    if (!functions) {
      throw new Error('Firebase Functions가 초기화되지 않았습니다.');
    }
    setIsCreating(true);
    try {
      const createEmployeeFromForm = httpsCallable(functions, 'createEmployeeFromForm');
      const result = await createEmployeeFromForm(data);
      // V65: 자동 채번된 사번 반환
      // V70.24-2: BE 가 반환하는 userId 를 보존한다.
      //   (생성 직후 프로필 사진 업로드 등 후속 작업에서 필요 — 이전에는 버려져 있었다)
      const resultData = result.data as any;
      return { userId: resultData?.userId, employeeNumber: resultData?.employeeNumber };
    } catch (error: any) {
      console.error('❌ 직원 생성 실패:', error);
      throw new Error(error.message || '직원 생성에 실패했습니다.');
    } finally {
      setIsCreating(false);
    }
  };

  /**
   * V17: 엑셀 일괄 업로드 - 청크 단위 순차 호출 (권장)
   *
   * V17 변경사항:
   * - 어떤 에러도 throw 하지 않고 항상 결과 반환 (인증/권한 에러 제외)
   * - 청크 에러 시 해당 청크의 모든 행을 개별 실패로 기록
   * - 불변식 보장: totalCount === successCount + failureCount
   * - 최종 로그에 📦 FINAL IMPORT RESULT 추가
   *
   * @param rawRows 원시 2D 배열 (엑셀에서 읽은 그대로)
   * @param onProgress 진행 상태 콜백 (선택)
   * @returns 최종 합산 결과 (항상 반환됨)
   */
  const bulkImportEmployeesWithChunks = async (
    rawRows: any[][],
    onProgress?: (progress: ImportProgress) => void
  ): Promise<BulkImportResult> => {
    if (!functions) {
      throw new Error('Firebase Functions가 초기화되지 않았습니다.');
    }

    setIsImporting(true);

    // V17: 최종 결과 초기화
    const finalResult: BulkImportResult = {
      totalCount: 0,
      successCount: 0,
      failureCount: 0,
      failures: [],
    };

    // V17: 마지막 처리된 청크 추적 (디버깅용)
    let lastProcessedChunkIndex = -1;

    try {
      // 1. 헤더 행 찾기
      const headerRowIndex = findHeaderRowIndex(rawRows);
      // 2. 데이터 행 추출 (객체 배열로 변환)
      const allDataRows = convertRawRowsToObjects(rawRows, headerRowIndex);

      if (allDataRows.length === 0) {
        console.warn('⚠️ V17: 유효한 데이터 행이 없습니다.');
        return finalResult;
      }

      // V17: totalCount는 실제 데이터 행 수로 미리 설정
      finalResult.totalCount = allDataRows.length;

      // 3. 청크로 분할
      const chunks = splitIntoChunks(allDataRows, CHUNK_SIZE);
      const totalChunks = chunks.length;

      // 4. 각 청크 순차 호출
      const bulkImportFn = httpsCallable<ChunkImportRequest, BulkImportResult>(
        functions,
        'bulkImportEmployeesFromExcel'
      );

      for (let i = 0; i < chunks.length; i++) {
        const chunk = chunks[i];
        // baseRowIndex: 헤더(headerRowIndex+1) + 1(1-based) + 청크 시작 위치
        const baseRowIndex = headerRowIndex + 2 + i * CHUNK_SIZE;
        // V17: chunkId 생성
        const chunkId = `${i + 1}/${totalChunks}`;

        // 진행 상태 콜백
        if (onProgress) {
          onProgress({
            currentChunk: i + 1,
            totalChunks: totalChunks,
            processedRows: i * CHUNK_SIZE,
            totalRows: allDataRows.length,
          });
        }

        try {
          const result = await bulkImportFn({
            dataRows: chunk,
            baseRowIndex,
            chunkId,
          });

          // V17: 응답 검증
          if (!result?.data) {
            throw new Error('Cloud Function 응답이 비어 있습니다.');
          }

          const chunkResult = result.data;

          // V17: 결과 합산 (백엔드가 보장한 값 사용)
          finalResult.successCount += chunkResult.successCount;
          finalResult.failureCount += chunkResult.failureCount;
          if (Array.isArray(chunkResult.failures)) {
            finalResult.failures.push(...chunkResult.failures);
          }

          lastProcessedChunkIndex = i;

        } catch (chunkError: any) {
          // V17: 인증/권한 에러면 즉시 중단하고 throw
          if (chunkError.code === 'unauthenticated' || chunkError.code === 'permission-denied') {
            console.error(`❌ V17 인증/권한 에러 - 중단: ${chunkError.message}`);
            throw chunkError;
          }

          // V17: 그 외 에러는 해당 청크의 모든 행을 개별 실패로 기록하고 계속
          console.error(`❌ V17 청크 ${chunkId} 실패:`, chunkError);

          const reason = chunkError.message || chunkError.code || '청크 처리 중 알 수 없는 오류가 발생했습니다.';

          // V17: 이 청크의 모든 행을 개별 실패로 기록
          for (let j = 0; j < chunk.length; j++) {
            const rowIndex = baseRowIndex + j;
            finalResult.failures.push({ rowIndex, reason });
          }
          finalResult.failureCount += chunk.length;

          lastProcessedChunkIndex = i;

          // V17: 루프 계속 (나머지 청크도 처리)
          continue;
        }
      }

      // 최종 진행 상태 콜백
      if (onProgress) {
        onProgress({
          currentChunk: totalChunks,
          totalChunks: totalChunks,
          processedRows: allDataRows.length,
          totalRows: allDataRows.length,
        });
      }

    } catch (error: any) {
      // V17: 인증/권한 에러만 throw, 나머지는 결과로 반환
      if (error.code === 'unauthenticated' || error.code === 'permission-denied') {
        console.error('❌ V17 인증/권한 에러:', error);
        setIsImporting(false);
        throw error;
      }

      // V17: 그 외 에러는 로그만 남기고, 지금까지 결과로 반환
      console.error(`❌ V17 예상치 못한 에러 (lastProcessedChunk=${lastProcessedChunkIndex}):`, error);

      // V17: 아직 처리 안 된 행들을 실패로 기록
      const processedRows = finalResult.successCount + finalResult.failureCount;
      const remainingRows = finalResult.totalCount - processedRows;
      if (remainingRows > 0) {
        const reason = `시스템 오류로 처리되지 않음: ${error.message || '알 수 없는 오류'}`;
        for (let k = 0; k < remainingRows; k++) {
          finalResult.failures.push({
            rowIndex: processedRows + k + 2, // +2 for header offset
            reason,
          });
        }
        finalResult.failureCount += remainingRows;
      }
    } finally {
      setIsImporting(false);
    }

    // V17: 불변식 검증 및 보정
    const computedTotal = finalResult.successCount + finalResult.failureCount;
    if (computedTotal !== finalResult.totalCount) {
      console.error(`⚠️ V17 불변식 위반! totalCount=${finalResult.totalCount}, but successCount(${finalResult.successCount}) + failureCount(${finalResult.failureCount}) = ${computedTotal}`);
      // V17: 강제 보정 (successCount + failureCount를 기준으로)
      finalResult.totalCount = computedTotal;
    }

    return finalResult;
  };

  /**
   * 엑셀 일괄 업로드 (레거시 - 단일 호출)
   *
   * ⚠️ 대용량 데이터는 bulkImportEmployeesWithChunks 사용 권장
   */
  const bulkImportEmployees = async (
    data: BulkImportExcelData
  ): Promise<BulkImportResult> => {
    if (!functions) {
      throw new Error('Firebase Functions가 초기화되지 않았습니다.');
    }

    // rawRows가 있으면 청크 방식 사용 (권장)
    if (data.rawRows && data.rawRows.length > 0) {
      return bulkImportEmployeesWithChunks(data.rawRows);
    }

    // 레거시: 단일 호출 (소량 데이터)
    setIsImporting(true);
    try {
      const bulkImportFn = httpsCallable<BulkImportExcelData, BulkImportResult>(
        functions,
        'bulkImportEmployeesFromExcel'
      );
      const result = await bulkImportFn(data);
      return result.data;
    } catch (error: any) {
      console.error('❌ 레거시 임포트 실패:', error);
      throw new Error(error.message || '직원 엑셀 업로드 중 오류가 발생했습니다.');
    } finally {
      setIsImporting(false);
    }
  };

  return {
    createEmployee,
    bulkImportEmployees,
    bulkImportEmployeesWithChunks, // V15: 청크 방식 (권장)
    isCreating,
    isImporting,
  };
}
